import React, { forwardRef, useCallback } from 'react';

import { Loading } from '@rover/icons';
import { Box, BoxProps, Flex, Text } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';

import { throwOrLogError } from '../../utils';

import {
  ButtonCommonProps,
  ButtonIconMarginMap,
  ButtonIconSizeMap,
  ButtonSizeTextSizeMap,
  getFullWidthPropForContainer,
  getVariant,
  getXPadding,
  getYPadding,
  InternalButtonVariants,
} from './Button.common';

export type Props = ButtonCommonProps;

// A copypaste of the original Rebass Button impl using our Box component
type ButtonBaseProps = Omit<BoxProps, 'variant'> & {
  variant?: InternalButtonVariants;
  children?: React.ReactNode;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  href?: string;
  webAs: 'a' | 'button';
};
const ButtonBase = forwardRef<HTMLElement, ButtonBaseProps>((props, ref) => (
  <Box
    {...props}
    ref={ref}
    tx="buttons"
    variant={props.variant}
    __css={{
      appearance: 'none',
      display: 'inline-block',
      textAlign: 'center',
      lineHeight: 'inherit',
      textDecoration: 'none',
      fontSize: 'inherit',
    }}
  />
));

const ButtonIcon = ({
  icon: Icon,
  // the destructuring of flexShrink prevents errors from it being passed to an <svg />
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  flexShrink,
  ...other
}: {
  icon: React.ComponentType<unknown>;
  flexShrink: string;
}): JSX.Element => {
  return <Icon {...other} />;
};

function Button({
  children,
  contrast = false,
  circular = false,
  destructive = false,
  disabled = false,
  fullWidth = false,
  href,
  icon,
  iconAlignment = 'left',
  isSelected = false,
  loading = false,
  selectedIcon,
  size = 'default',
  variant = 'default',
  justifyContent = 'center',
  sx,
  roleOverride,
  webProps = {},
  onPress,
  onClick,

  // pull out rxnProps and do nothing with them because this is the web impl
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  rxnProps,

  ...other
}: Props): JSX.Element {
  const { type = 'button', hrefTarget, ref, variant: webPropsVariant, ...otherWebProps } = webProps;
  // Check for accessibility props in both webProps and other props (top-level)
  const hasAriaLabel = otherWebProps['aria-label'] || other['aria-label'];
  const hasAriaLabelledBy = otherWebProps['aria-labelledby'] || other['aria-labelledby'];
  if (!children && !hasAriaLabel && !hasAriaLabelledBy) {
    throwOrLogError('Button.props MUST have children, or a11y values');
  }
  if (selectedIcon && icon && iconAlignment !== 'right') {
    throwOrLogError(
      'Button.props selectedIcon with a static icon is only supported with iconAlignment="right"'
    );
  }
  if (
    isSelected &&
    !['aria-checked', 'aria-pressed', 'aria-selected'].some((p) => Object.keys(other).includes(p))
  ) {
    throwOrLogError(
      'Button.props isSelected requires one of aria-checked, aria-pressed, or aria-selected to be true'
    );
  }

  const isLink = Boolean(href);
  const resolvedVariant = webPropsVariant || variant;
  const theme = useTheme();

  // Get theme color for links to override browser defaults
  const buttonTextColor = (() => {
    if (!isLink) return '';
    const variantKey = getVariant(disabled, destructive, contrast, variant);
    const [variantName, stateName] = variantKey.split('.');
    const buttonTheme = theme.buttons?.[variantName]?.[stateName] as { color?: string } | undefined;
    return buttonTheme?.color || '';
  })();

  const handleClick = useCallback(
    async (hybridEvent) => {
      // guard against clicks when disabled (mobile browsers may not honor the HTML attribute)
      if (disabled) return;

      // if no press handler or we are not on the client, allow normal href behavior
      if ((!onPress && !onClick) || (!process.env.JS_ENV_CLIENT && process.env.NODE_ENV !== 'test'))
        return;

      // Extract the web event from the hybrid format
      const webEvent = hybridEvent?.webEvent;

      // prevent default behavior of navigating to href (only for web events that have preventDefault)
      if (webEvent && typeof webEvent.preventDefault === 'function') {
        webEvent.preventDefault();
      }

      // await the onPress handler before navigating to href
      try {
        // If onPress is being used, pass the hybrid format; if onClick, call with no args
        if (onPress) {
          await onPress(hybridEvent);
        } else if (onClick) {
          await onClick();
        }
      } finally {
        if (isLink && href !== '') window.open(href, hrefTarget || '_self');
      }
    },
    [disabled, onPress, onClick, href, isLink, hrefTarget]
  );

  const commonIconProps = {
    fill: 'currentColor',
    height: ButtonIconSizeMap[size],
    width: ButtonIconSizeMap[size],
  };

  const styledIcon = icon ? (
    <Box
      {...commonIconProps}
      webAs={ButtonIcon}
      flexShrink="0"
      data-testid="kc-button-icon"
      // @ts-expect-error NOTE(Ryan) This is an odd usage of Box and fixing it isn't worth it right now
      icon={icon}
    />
  ) : undefined;

  const styling = isLink
    ? {
        '&, &:link, &:visited, &:focus, &:hover, &:active': {
          'text-decoration': 'none !important',
          color: `${buttonTextColor} !important`,
        },
      }
    : {};

  return (
    <ButtonBase
      variant={getVariant(disabled, destructive, contrast, resolvedVariant)}
      disabled={disabled}
      // @ts-expect-error - allow nonstandard padding to get proper button dimensions
      px={getXPadding(size, circular, resolvedVariant)}
      // @ts-expect-error - allow nonstandard padding to get proper button dimensions
      py={getYPadding(size, resolvedVariant)}
      type={isLink ? undefined : type}
      webAs={isLink ? 'a' : 'button'}
      role={roleOverride ?? (isLink ? 'link' : 'button')}
      href={disabled || !isLink ? undefined : href}
      target={disabled || !isLink ? undefined : hrefTarget}
      onPress={handleClick}
      {...getFullWidthPropForContainer(fullWidth, resolvedVariant)}
      sx={{ outlineOffset: '3px', ...styling, ...sx }}
      ref={ref}
      {...other}
      {...otherWebProps}
    >
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent={justifyContent}
        gap={other.childrenFlexGap || ButtonIconMarginMap[size]}
      >
        {(!circular || !loading) && icon && iconAlignment === 'left' && styledIcon}
        {(!circular || !loading) && isSelected && selectedIcon && (
          <Box
            {...commonIconProps}
            webAs={ButtonIcon}
            flexShrink="0"
            data-testid="kc-button-selectedicon"
            // @ts-expect-error NOTE(Ryan) This is an odd usage of Box and fixing it isn't worth it right now
            icon={selectedIcon}
          />
        )}
        {children && (
          <Text
            size={ButtonSizeTextSizeMap[size]}
            fontWeight="semibold"
            data-testid="kc-button-text"
          >
            {children}
          </Text>
        )}
        {(!circular || !loading) && icon && iconAlignment === 'right' && styledIcon}
        {loading && (
          <Box
            flexShrink="0"
            height={ButtonIconSizeMap[size]}
            width={ButtonIconSizeMap[size]}
            data-testid="kibble-button-loading-spinner"
          >
            <Loading {...commonIconProps} />
          </Box>
        )}
      </Flex>
    </ButtonBase>
  );
}

/**
 * Button is an interactive component that triggers actions when clicked.
 * Supports loading states, icons, variants, links, and accessibility features.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/3836346394/Kibble+Core+Button
 */
export default Button;
